import React, { useState, useEffect } from 'react';
import { Shield, QrCode, Check, X, AlertTriangle, Loader } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const Admin2FA = () => {
  const { user, fetchUser } = useAuth();
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [step, setStep] = useState('loading');

  // Fetch actual 2FA status from backend on page load
  useEffect(() => {
    const check2FAStatus = async () => {
      try {
        const res = await api.get('/admin/2fa/status');
        setStep(res.data.two_fa_enabled ? 'enabled' : 'setup');
      } catch (error) {
        console.error('Failed to check 2FA status:', error);
        setStep('setup');
      } finally {
        setCheckingStatus(false);
      }
    };
    check2FAStatus();
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const response = await api.post('/admin/2fa/setup');
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setStep('verify');
    } catch (error) {
      if (error.response?.data?.detail?.includes('already enabled')) {
        toast.error('2FA is already enabled. Refresh the page.');
        setStep('enabled');
      } else {
        toast.error('Failed to setup 2FA');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/2fa/verify', { code });
      toast.success('2FA enabled successfully!');
      setStep('enabled');
      if (fetchUser) fetchUser();
    } catch (error) {
      toast.error('Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (code.length !== 6) {
      toast.error('Please enter your current 2FA code');
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/2fa/disable', { code });
      toast.success('2FA disabled');
      setStep('setup');
      setCode('');
      if (fetchUser) fetchUser();
    } catch (error) {
      toast.error('Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-2fa-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Shield className="w-12 h-12 text-primary" />
          <div>
            <h1 className="text-4xl font-bold">Two-Factor Authentication</h1>
            <p className="text-muted-foreground">Secure your admin account with 2FA</p>
          </div>
        </div>

        <div className="glass-card p-8">
          {checkingStatus ? (
            <div className="text-center py-8">
              <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">Checking 2FA status...</p>
            </div>
          ) : step === 'setup' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-yellow-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2">2FA Not Enabled</h2>
              <p className="text-muted-foreground mb-6">
                Add an extra layer of security to your admin account using Google Authenticator
              </p>
              <button
                onClick={handleSetup}
                disabled={loading}
                className="btn-primary px-8"
                data-testid="setup-2fa-btn"
              >
                {loading ? 'Setting up...' : 'Setup 2FA Now'}
              </button>
            </div>
          )}

          {step === 'verify' && (
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-6">Scan QR Code</h2>
              
              <div className="bg-white p-4 rounded-xl inline-block mb-6">
                {qrCode && <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />}
              </div>

              <div className="text-left p-4 bg-white/5 rounded-lg mb-6">
                <p className="text-sm text-muted-foreground mb-2">
                  1. Download Google Authenticator app on your phone
                </p>
                <p className="text-sm text-muted-foreground mb-2">
                  2. Scan the QR code above with the app
                </p>
                <p className="text-sm text-muted-foreground">
                  3. Enter the 6-digit code from the app below
                </p>
              </div>

              {secret && (
                <div className="p-3 bg-primary/10 rounded-lg mb-6">
                  <p className="text-xs text-muted-foreground mb-1">Manual entry key (backup):</p>
                  <code className="text-sm font-mono">{secret}</code>
                </div>
              )}

              <div className="max-w-xs mx-auto">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-center text-2xl tracking-widest mb-4"
                  maxLength={6}
                  data-testid="2fa-code-input"
                />
                <button
                  onClick={handleVerify}
                  disabled={loading || code.length !== 6}
                  className="w-full btn-primary"
                >
                  {loading ? 'Verifying...' : 'Verify & Enable'}
                </button>
              </div>
            </div>
          )}

          {step === 'enabled' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-green-500">2FA is Active</h2>
              <p className="text-muted-foreground mb-8">
                Your admin account is protected with two-factor authentication
              </p>

              <div className="border-t border-white/10 pt-6">
                <h3 className="font-semibold mb-4 text-left">Disable 2FA</h3>
                <p className="text-sm text-muted-foreground mb-4 text-left">
                  Enter your current authenticator code to disable 2FA
                </p>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-digit code"
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-center tracking-widest"
                    maxLength={6}
                  />
                  <button
                    onClick={handleDisable}
                    disabled={loading || code.length !== 6}
                    className="px-6 py-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all"
                  >
                    Disable
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin2FA;
